import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import { theme, GlobalToken } from 'antd';

import unitsEnum from '@/constants/unitsEnum';
import { SpinCentered } from '@/comp/index';
import Super from '@/comp/Super';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useStores } from '@/hooks/use-stores';
import getYearlyhGraphInitOptions from '@/comp/pages/ValueDashboardPage/YearlyhGraphInitOptions';

import StationRiskYearlyGraphTooltip from './StationRiskYearlyGraphToolTip';

const { useToken } = theme;

function getMonthFromString(mon) {
  const d = moment(mon, 'MMM');
  if (!d.isValid()) return -1;
  return Number(d.format('M')) - 1;
}

function getYBySeries(series, month) {
  return series && series.find(point => point.x === getMonthFromString(month))?.y;
}

const StationRiskYearlyGraph = observer(({ yearToPresent }) => {
  const { t } = useTranslation();
  const { stationRiskGraphStore } = useStores();
  const { token } = useToken();
  const [options, setOptions] = useState(getYearlyhGraphInitOptions(t, token));
  const { tunit, tvalue } = useMeasurement();

  const categories = () => {
    const categories = [];
    const monthsArray = [...Array(12).keys()];
    monthsArray.map(i => categories.push(moment().month(i).format('MMM')));
    return categories;
  };

  useEffect(() => {
    stationRiskGraphStore.getGraphByYear(yearToPresent);
  }, [stationRiskGraphStore, yearToPresent]);

  function getLimitedPeakProduction() {
    /**
     * Handles error cases where the peak production exceeds the available production.
     * Will return the minimal one of the two, to avoid a peak line above the graph.
     * */

    const DASHED_LINE_WIDTH_COMPENSATION = 0.2;
    const plannedAvailabilitySeries = stationRiskGraphStore.stationRiskGraphData?.plannedAvailabilitySeries || [];

    return stationRiskGraphStore.stationRiskGraphData?.peakProductionSeries?.map((point, i) => {
      if (point?.y == null) return null;
      if (plannedAvailabilitySeries[i]?.y == null) return 0;

      return {
        x: point.x,
        y: tvalue(Math.min(point.y, plannedAvailabilitySeries[i].y - DASHED_LINE_WIDTH_COMPENSATION), unitsEnum.m3Min),
      };
    });
  }

  useEffect(() => {
    const newOptions = {
      ...getYearlyhGraphInitOptions(t, token),
      xAxis: {
        lineWidth: 2,
        lineColor: token.colorBgContainer,
        tickColor: token.colorBgContainer,
        showEmpty: true,
        labels: {
          formatter: function () {
            if (yearToPresent === moment().year() && this.pos === moment().month()) {
              return ReactDOMServer.renderToStaticMarkup(
                <span>
                  {t(this.value)}
                  <span className="asterisk"> *</span>
                </span>,
              );
            } else {
              return t(this.value);
            }
          },
          style: {
            fontSize: '7.7px',
            color: token.colorTextSecondary,
          },
        },
        categories: categories(),
        type: 'category',
        min: 0,
        max: 11,
      },
      yAxis: [
        {
          gridLineColor: token.colorBorderSecondary,
          lineColor: token.colorBorderSecondary,
          gridLineDashStyle: 'longdash',
          lineWidth: 2,
          title: {
            useHTML: true,
            text: ReactDOMServer.renderToString(
              <Trans i18nKey={tunit('m3/min')}>
                <Super />
              </Trans>,
            ),
            align: 'high',
            rotation: 0,
            y: -25,
            offset: -15,
            style: {
              color: token.colorTextSecondary,
              fontSize: '9.6px',
            },
          },
          labels: {
            style: {
              fontSize: '9.6px',
              color: token.colorTextSecondary,
            },
            x: -9,
          },
          min: 0,
        },
      ],
      tooltip: {
        shared: true,
        shadow: false,
        shape: 'square',
        useHTML: true,
        padding: 0,
        backgroundColor: undefined,
        borderWidth: 0,
        borderRadius: 0,
        formatter: function () {
          const monthName = this.x;
          return ReactDOMServer.renderToString(
            <StationRiskYearlyGraphTooltip
              monthAvgProduction={getYBySeries(
                stationRiskGraphStore?.stationRiskGraphData?.avgSupplyFlowSeries,
                monthName,
              )}
              monthPeakProduction={getYBySeries(
                stationRiskGraphStore?.stationRiskGraphData?.peakProductionSeries,
                monthName,
              )}
              monthHighRisk={getYBySeries(stationRiskGraphStore?.stationRiskGraphData?.highRiskSeries, monthName)}
              monthModerateRisk={getYBySeries(
                stationRiskGraphStore?.stationRiskGraphData?.moderateRiskSeries,
                monthName,
              )}
              monthLowRisk={getYBySeries(stationRiskGraphStore?.stationRiskGraphData?.healthyRangeSeries, monthName)}
              monthDegradation={getYBySeries(stationRiskGraphStore?.stationRiskGraphData?.degradationSeries, monthName)}
              monthPlannedAvailability={getYBySeries(
                stationRiskGraphStore?.stationRiskGraphData?.plannedAvailabilitySeries,
                monthName,
              )}
            />,
          );
        },
      },
      plotOptions: {
        column: {
          borderWidth: 0,
          grouping: false,
        },
        series: {
          pointWidth: 30,
          stacking: 'normal',
          lineWidth: 1,
        },
      },
      series: [
        {
          // Series to create the legend item of the avg graph
          name: t('Avg Production'),
          type: 'line',
          legendIndex: 2,
          marker: {
            enabled: false,
          },
          visible: true,
          zIndex: 5,
          color: token.colorText,
        },
        {
          // Series to create the top vertical line of the avg graph
          linkedTo: `:previous`,
          name: 'avg_vertical_line',
          color: token.colorText,
          type: 'column',
          maxPointWidth: 1,
          visible: true,
          stack: 1,
          data: stationRiskGraphStore.stationRiskGraphData?.avgSupplyFlowSeries.map(point => ({
            x: point.x,
            y: tvalue(point.y, unitsEnum.m3Min),
          })),
          zIndex: 3,
        },
        {
          // Series to create the top horizontal line of the avg graph
          linkedTo: `:previous`,
          name: 'avg_horizontal_line',
          color: token.colorText,
          type: 'line',
          lineWidth: 0,
          marker: {
            symbol: 'line',
            lineColor: token.colorText,
            lineWidth: 1,
            radius: 2,
          },

          visible: true,
          stack: 1,
          data: stationRiskGraphStore.stationRiskGraphData?.avgSupplyFlowSeries.map(point => ({
            x: point.x,
            y: tvalue(point.y, unitsEnum.m3Min),
          })),
          zIndex: 3,
        },
        {
          name: 'Dark Overlay',
          color: `rgba(0, 0, 0, 0.3)`,
          showInLegend: false,
          type: 'columnrange',
          visible: true,
          stack: 2,
          borderWidth: 0,
          data: getLimitedPeakProduction()?.map((point, i) => {
            if (!point || !('x' in point)) return point;
            return [
              point.x,
              point.y,
              tvalue(stationRiskGraphStore.stationRiskGraphData?.plannedAvailabilitySeries[i].y - 0.2, unitsEnum.m3Min),
              //TODO-0.2 is to compensate for the overlay being a bit too big. Need to find a way to fix it.
            ];
          }),
          zIndex: 2,
        },
        {
          name: 'Degradation',
          showInLegend: false,
          color: token.colorBgElevated,
          type: 'column',
          data: stationRiskGraphStore.stationRiskGraphData?.degradationSeries.map(point => ({
            x: point.x,
            y: tvalue(point.y, unitsEnum.m3Min),
          })),
          stack: 0,
          zIndex: 1,
        },
        {
          name: 'High Risk',
          showInLegend: false,
          color: token.colorError,
          type: 'column',
          data: stationRiskGraphStore.stationRiskGraphData?.highRiskSeries.map(point => ({
            x: point.x,
            y: tvalue(point.y, unitsEnum.m3Min),
          })),
          stack: 0,
          zIndex: 1,
        },
        {
          name: 'Moderate Risk',
          showInLegend: false,
          color: token.colorWarning,
          type: 'column',
          data: stationRiskGraphStore.stationRiskGraphData?.moderateRiskSeries.map(point => ({
            x: point.x,
            y: tvalue(point.y, unitsEnum.m3Min),
          })),
          stack: 0,
          zIndex: 1,
        },
        {
          name: 'Healthy Range',
          showInLegend: false,
          color: token.colorSuccess,
          type: 'column',
          stack: 0,
          data: stationRiskGraphStore.stationRiskGraphData?.healthyRangeSeries.map(point => ({
            x: point.x,
            y: tvalue(point.y, unitsEnum.m3Min),
          })),
          zIndex: 1,
        },
        {
          name: t('Peak Production'),
          type: 'line',
          legendIndex: 2,
          lineWidth: 0,
          marker: {
            symbol: 'dashedLineLegend',
            lineColor: token.colorText,
            lineWidth: 1,
            radius: 10,
          },
          visible: true,
          zIndex: 5,
          color: token.colorText,
        },
        {
          linkedTo: `:previous`,
          name: 'peak_prod_line',
          data: getLimitedPeakProduction(),
          type: 'line',
          color: token.colorText,
          lineWidth: 0,
          showInLegend: false,

          marker: {
            symbol: 'dashedLine',
            lineColor: token.colorText,
            lineWidth: 1,
            radius: 15,
          },
          zIndex: 5,
        },
      ],
    };
    setOptions(newOptions);
  }, [stationRiskGraphStore.stationRiskGraphData, token, t]);

  const getDashedLineSvg = (x, y, w, h, legend) => {
    const LEGEND_SYMBOL_WIDTH = 12;
    const START_POINT_OFFSET = 2;
    w = legend ? LEGEND_SYMBOL_WIDTH : w;
    const returnArray = [];
    const startPoint = x + START_POINT_OFFSET;
    const dashAmount = 5;
    const dashAndGapWidth = w / dashAmount / 2;
    const yValWithOffset = y + h / 2; //account for marker radius when moving to the right Y position
    let currentPoint = startPoint;

    for (let i = 0; i < dashAmount; ++i) {
      returnArray.push('M', currentPoint, yValWithOffset, 'L', currentPoint + dashAndGapWidth, yValWithOffset);
      currentPoint += dashAndGapWidth + dashAndGapWidth;
    }
    returnArray.push('z');
    return returnArray;
  };

  Highcharts.SVGRenderer.prototype.symbols.dashedLine = function (x, y, w, h) {
    return getDashedLineSvg(x, y, w, h, false);
  };

  Highcharts.SVGRenderer.prototype.symbols.dashedLineLegend = function (x, y, w, h) {
    return getDashedLineSvg(x, y, w, h, true);
  };

  Highcharts.SVGRenderer.prototype.symbols.line = function (x, y, w) {
    const returnArray = [];
    const startPoint = x - 0.8 * w;
    const endPoint = x + 2 * w;

    const yValWithOffset = y + w / 2; //account for marker radius when moving to the right Y position
    returnArray.push('M', startPoint, yValWithOffset, 'L', endPoint, yValWithOffset);
    returnArray.push('z');
    return returnArray;
  };

  return (
    <Container>
      {stationRiskGraphStore.isLoading ? (
        <SpinCentered className={'spinner'} />
      ) : (
        <HighchartsReact highcharts={Highcharts} options={options} />
      )}
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;

  .asterisk {
    fill: ${({ theme: { token } }) => token.colorError};
  }
`;

export default StationRiskYearlyGraph;
